import { useQuery } from '@tanstack/react-query';
import { SearchPicker, type SearchPickerStatus } from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { listItems } from '../api/finance.ts';
import { CATEGORY_LABELS } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './accounting.module.css';

const MIN_CHARS = 2;

export interface PickedService {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, ms]);
  return debounced;
}

/** Type-to-search for one catalogue service a revenue share rule applies to (DESIGN.md §7). */
export function ServicePicker({ onSelect }: { onSelect: (item: PickedService) => void }) {
  const online = useOnline();
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const debounced = useDebounced(trimmed, 200);
  const search = useQuery({
    queryKey: ['revenue-share', 'service-search', debounced],
    queryFn: () => listItems({ q: debounced, status: 'active' }, 20),
    enabled: online && debounced.length >= MIN_CHARS,
    staleTime: 10_000,
    retry: false,
  });
  let status: SearchPickerStatus;
  if (trimmed.length < MIN_CHARS) status = 'idle';
  else if (!online) status = 'offline';
  else if (debounced !== trimmed || search.isFetching || search.isPending) status = 'searching';
  else if (search.isError) status = 'error';
  else status = 'ready';
  return (
    <SearchPicker
      label="Service"
      description="Type a name or code."
      placeholder="Name or code"
      inputValue={text}
      onInputChange={setText}
      status={status}
      items={search.data?.items ?? []}
      itemsQuery={debounced}
      getKey={(item) => item.id}
      getTextValue={(item) => `${item.name} ${item.code}`}
      renderItem={(item) => (
        <span className={styles.pickerRow}>
          <span className={styles.pickerName}>{item.name}</span>
          <span>
            <span className="wodi-id">{item.code}</span> {CATEGORY_LABELS[item.category]}
          </span>
        </span>
      )}
      isExactMatch={(item, query) => item.code.toUpperCase() === query.toUpperCase()}
      onSelect={(item) => {
        setText('');
        onSelect({ id: item.id, code: item.code, name: item.name });
      }}
      promptMessage="Type at least two letters of the service."
      emptyMessage="No service matches. Check the spelling, or choose a category instead."
      {...(search.error === null
        ? {}
        : {
            errorMessage:
              search.error instanceof ApiError
                ? search.error.message
                : 'Search did not work. Try again.',
          })}
      onRetry={() => void search.refetch()}
      offlineMessage="Searching services needs a connection to Wodi."
      countMessage={(n) => `${String(n)} ${n === 1 ? 'service' : 'services'} found`}
    />
  );
}
